import { createContext, useContext, useEffect, useRef, type RefObject } from "react";
import { useLocation } from "react-router-dom";

const STORE_KEY = "blogic:pageScroll";
const MAX_ENTRIES = 30;

export const ScrollRootContext = createContext<RefObject<HTMLElement | null> | null>(null);

export const FRESH_PAGE_STATE = { freshPage: true } as const;

export function isFreshPageState(state: unknown): boolean {
  return (state as Partial<typeof FRESH_PAGE_STATE> | null)?.freshPage === true;
}

let returningTo: string | null = null;

function readStore(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, number>): boolean {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
    return true;
  } catch {
    return false;
  }
}

export function scrollTopOf(root: HTMLElement | null | undefined): number {
  return Math.max(window.scrollY, root?.scrollTop ?? 0);
}

export function scrollRootTo(root: HTMLElement | null | undefined, top: number) {
  root?.scrollTo({ top });
  window.scrollTo({ top });
}

export function rememberPageScroll(url: string, top: number) {
  const store = readStore();
  delete store[url];
  if (top > 0) store[url] = top;
  const urls = Object.keys(store);
  for (const stale of urls.slice(0, Math.max(0, urls.length - MAX_ENTRIES))) delete store[stale];
  writeStore(store);
}

export function markReturningTo(url: string | null) {
  returningTo = url;
}

export function useRestoreListScroll(ready: boolean, enabled = true) {
  const root = useContext(ScrollRootContext);
  const { pathname, search } = useLocation();
  const url = `${pathname}${search}`;
  const handled = useRef(false);

  useEffect(() => {
    if (!enabled || !ready || handled.current) return;
    handled.current = true;
    const isReturn = returningTo === url;
    returningTo = null;
    if (!isReturn) return;
    const top = readStore()[url];
    if (!top) return;
    requestAnimationFrame(() => scrollRootTo(root?.current, top));
  }, [enabled, ready, url, root]);
}
